import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import { Store, RefreshCw, Search, FileDown } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { useAuth } from '../context/AuthContext';
import CompetitorDiscountWatch from './CompetitorDiscountWatch';
import { nameMismatch as mismatch } from '../utils/competitorMatchSanity';

// High5 only — price position per seller × brand, with the product list behind every number.
// Same data as /high5/price-comparison (/api/competitor-intel/comparison), stricter rules:
//  - out-of-stock products are dropped everywhere (ours: 0 sizes in the SKU feed; theirs:
//    out of stock at the crawl or 0 sizes)
//  - sellers with size data (Superstep, Intersport, SPX, Vakkorama) are compared only where
//    they have at least 2 sizes in stock; sellers without size data on every in-stock match
//  - a difference under ₺1 counts as equal (SPX prices end in ,99)

const API_BASE = 'https://api.glowytics.com';
const MIN_SIZES = 2;
const EQ_TL = 1;

const fmt = (v) =>
  v == null ? '—' : `₺${Number(v).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
const fmtNum = (n) => Number(n || 0).toLocaleString('en-US');
const rel = (ours, theirs) =>
  Math.abs(ours - theirs) < EQ_TL ? 'equal' : ours < theirs ? 'cheaper' : 'pricier';

const REL_META = {
  cheaper: { label: 'We are cheaper', cls: 'text-emerald-600' },
  equal: { label: 'Equal', cls: 'text-gray-500' },
  pricier: { label: 'We are pricier', cls: 'text-red-600' },
  matched: { label: 'Matched', cls: 'text-gray-900' },
};
const MKT_META = {
  cheapest: { label: 'Cheapest in market', cls: 'text-emerald-600' },
  range: { label: 'Within market range', cls: 'text-gray-500' },
  priciest: { label: 'Priciest in market', cls: 'text-red-600' },
  matched: { label: 'Matched', cls: 'text-gray-900' },
};
const MATCH_LABEL = { gtin: 'barcode', code: 'style code', name: 'model+color', model_gender: 'model+gender' };

export default function High5SellerPriceMatrix() {
  const { selectedBrand } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [brand, setBrand] = useState('');
  // { site: '<seller>' | '_market', rel: 'cheaper'|'equal'|'pricier'|'matched' | cheapest|range|priciest }
  const [pick, setPick] = useState({ site: '_market', rel: 'priciest' });
  const [search, setSearch] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/competitor-intel/comparison`, {
        credentials: 'include',
        headers: { 'X-Brand-ID': selectedBrand?.id || localStorage.getItem('currentBrandId') || '' },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
    } catch (err) {
      setError(err.message);
      toast.error('Could not load comparison. ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedBrand?.id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const OUR = data?.ourSite || 'High5';
  const SITES = useMemo(() => data?.compSites || [], [data]);
  // A seller "has size data" when any of its matches carries a size count.
  const SIZED = useMemo(() => {
    const s = new Set();
    for (const r of data?.rows || []) for (const k of SITES) if (r.sizes?.[k]?.avail != null) s.add(k);
    return s;
  }, [data, SITES]);

  // One entry per in-stock product of ours, with every seller offer classified.
  const items = useMemo(() => {
    const out = [];
    for (const r of data?.rows || []) {
      const own = r.sizes?.[OUR];
      if (own && own.avail === 0) continue; // we are out of stock
      const ours = r.prices?.[OUR];
      if (ours == null) continue;
      const offers = {};
      for (const s of SITES) {
        const ex = r.excluded?.[s];
        const price = r.prices?.[s] ?? ex?.price;
        if (price == null) continue;
        const sz = r.sizes?.[s];
        if (ex?.reason === 'stock' || sz?.avail === 0) continue; // they are out of stock
        // wrong match (gender / trail / type) — barcode matches are trusted even if named differently
        if (r.methods?.[s] !== 'gtin' && mismatch(r.name, r.compNames?.[s])) continue;
        const sized = SIZED.has(s);
        const counted = sized ? sz?.avail != null && sz.avail >= MIN_SIZES : true;
        offers[s] = { price, sz, sized, counted, url: r.urls?.[s], method: r.methods?.[s],
          rel: counted ? rel(ours, price) : null };
      }
      const cnt = Object.values(offers).filter((o) => o.counted).map((o) => o.price);
      let market = null;
      if (cnt.length) {
        const lo = Math.min(...cnt), hi = Math.max(...cnt);
        market = ours <= lo - EQ_TL ? 'cheapest' : ours >= hi + EQ_TL ? 'priciest' : 'range';
      }
      out.push({ ...r, ours, own, offers, market,
        marketMin: cnt.length ? Math.min(...cnt) : null, marketMax: cnt.length ? Math.max(...cnt) : null,
        bkey: (r.brandNorm || r.brand || '').toLowerCase() });
    }
    return out;
  }, [data, OUR, SITES, SIZED]);

  // Brand summary (all brands)
  const brandRows = useMemo(() => {
    const m = new Map();
    for (const it of items) {
      const z = m.get(it.brand) || { brand: it.brand, products: 0, matched: 0, cheapest: 0, range: 0, priciest: 0 };
      z.products++;
      if (it.market) { z.matched++; z[it.market]++; }
      m.set(it.brand, z);
    }
    return [...m.values()].filter((b) => b.matched > 0).sort((a, b) => b.matched - a.matched);
  }, [items]);

  useEffect(() => {
    if (!brand && brandRows.length) setBrand(brandRows[0].brand);
  }, [brand, brandRows]);

  const brandItems = useMemo(() => items.filter((x) => x.brand === brand), [items, brand]);
  const bkey = brandItems[0]?.bkey || (brand || '').toLowerCase();

  // Seller table for the selected brand
  const sellerRows = useMemo(() => SITES.map((s) => {
    const z = { site: s, sized: SIZED.has(s), matched: 0, counted: 0, cheaper: 0, equal: 0, pricier: 0,
      stock: data?.sellerStock?.[s]?.[bkey]?.total ?? null };
    for (const it of brandItems) {
      const o = it.offers[s];
      if (!o) continue;
      z.matched++;
      if (o.counted) { z.counted++; z[o.rel]++; }
    }
    return z;
  }).filter((z) => z.matched > 0 || z.stock), [SITES, SIZED, brandItems, data, bkey]);

  // Product list for the current pick
  const list = useMemo(() => {
    let r;
    if (pick.site === '_market') {
      r = brandItems.filter((it) => it.market && (pick.rel === 'matched' || it.market === pick.rel));
      r = r.map((it) => ({ it, theirs: it.marketMin, gap: it.marketMin ? (it.ours / it.marketMin - 1) * 100 : null }));
    } else {
      r = brandItems.filter((it) => {
        const o = it.offers[pick.site];
        if (!o) return false;
        if (pick.rel === 'matched') return true;
        return o.counted && o.rel === pick.rel;
      }).map((it) => {
        const o = it.offers[pick.site];
        return { it, o, theirs: o.price, gap: (it.ours / o.price - 1) * 100 };
      });
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      r = r.filter(({ it }) => (it.name || '').toLowerCase().includes(q) || (it.productId || '').toLowerCase().includes(q));
    }
    return r.sort((a, b) => (b.gap ?? -1e9) - (a.gap ?? -1e9));
  }, [brandItems, pick, search]);

  const pickLabel = pick.site === '_market'
    ? `${brand} · all sellers · ${MKT_META[pick.rel].label}`
    : `${brand} · ${pick.site} · ${REL_META[pick.rel].label}`;

  const downloadCsv = () => {
    const isMkt = pick.site === '_market';
    const head = ['Brand', 'Product', 'Product ID', 'High5 price', 'High5 sizes',
      isMkt ? 'Market min' : `${pick.site} price`, isMkt ? 'Market max' : `${pick.site} sizes`,
      'Difference (TL)', 'Difference (%)', isMkt ? 'Cheapest seller' : 'Match', isMkt ? '' : `${pick.site} link`, 'High5 link'];
    const rows = list.map(({ it, o, theirs, gap }) => {
      const cheapest = isMkt ? Object.entries(it.offers).filter(([, x]) => x.counted && x.price === it.marketMin).map(([s]) => s).join(' / ') : '';
      return [it.brand, it.name, it.productId, it.ours, it.own ? `${it.own.avail}/${it.own.total}` : '',
        theirs, isMkt ? it.marketMax : (o.sz?.avail != null ? `${o.sz.avail}/${o.sz.total ?? ''}` : 'no data'),
        Math.round(it.ours - theirs), gap == null ? '' : gap.toFixed(1),
        isMkt ? cheapest : (MATCH_LABEL[o.method] || o.method || ''), isMkt ? '' : (o.url || ''), it.urls?.[OUR] || ''];
    });
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const csv = '﻿' + [head, ...rows].map((r) => r.map(esc).join(';')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    a.download = `high5-${brand}-${pick.site === '_market' ? 'market' : pick.site}-${pick.rel}.csv`.toLowerCase();
    a.click();
  };

  if (loading) {
    return (
      <div className="p-6 max-w-[1320px] mx-auto">
        <Card className="rounded-2xl border border-gray-100 shadow-sm p-10 flex items-center justify-center text-gray-500">
          <RefreshCw className="w-5 h-5 mr-2 animate-spin" /> Loading…
        </Card>
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-6 max-w-[1320px] mx-auto">
        <Card className="rounded-2xl border border-red-100 shadow-sm p-8 text-red-600">
          Error: {error}
          <button onClick={fetchData} className="ml-3 underline">Try again</button>
        </Card>
      </div>
    );
  }

  const Num = ({ n, site, r, cls }) => {
    const active = pick.site === site && pick.rel === r;
    if (!n) return <span className="text-gray-300">0</span>;
    return (
      <button type="button" onClick={() => setPick({ site, rel: r })}
        className={`tabular-nums px-2 py-0.5 rounded-md ${active ? 'bg-gray-900 text-white' : `${cls} hover:bg-gray-100 underline decoration-dotted underline-offset-4`}`}>
        {fmtNum(n)}
      </button>
    );
  };

  return (
    <div className="p-6 space-y-6 max-w-[1320px] mx-auto">
      <Card className="rounded-2xl overflow-hidden shadow-xl border border-gray-100">
        <div className="p-6 bg-white">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-indigo-50 border border-indigo-100 flex items-center justify-center">
                <Store className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Price by Seller</h1>
                <p className="text-sm text-gray-500 mt-1 max-w-3xl">
                  Out-of-stock products are left out everywhere, ours and the sellers&apos;. Sellers with size data
                  ({[...SIZED].join(', ')}) count only when they have at least {MIN_SIZES} sizes in stock; sellers
                  without size data count on every in-stock match. A difference under ₺{EQ_TL} counts as equal. Matches whose names disagree on gender, Trail vs road model or product type are left out.
                  Click any number to see the products behind it.
                  {data?.dataDates && (
                    <span className="ml-1 text-gray-400">
                      · Data: {Object.entries(data.dataDates).map(([s, d]) => `${s} ${d}`).join(' · ')}
                    </span>
                  )}
                </p>
              </div>
            </div>
            <button type="button" onClick={fetchData} title="Refresh"
              className="flex items-center justify-center w-10 h-10 bg-white border rounded-xl text-gray-600 hover:bg-gray-50">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>
      </Card>

      {/* Competitor discount alerts + daily chart for the selected brand */}
      {bkey && <CompetitorDiscountWatch brandKey={bkey} />}

      {/* Brand position vs the whole market */}
      <Card className="rounded-2xl overflow-hidden shadow-sm border border-gray-100">
        <div className="px-5 py-3 bg-gray-50 border-b border-gray-100 text-sm font-semibold text-gray-700">
          Position by brand <span className="font-normal text-gray-400">(our price vs all counted sellers · click a brand to see its sellers)</span>
        </div>
        <div className="overflow-auto max-h-96">
          <table className="min-w-full text-sm">
            <thead className="bg-white sticky top-0 z-10">
              <tr className="text-gray-500 border-b border-gray-100">
                <th className="text-left px-5 py-2.5 font-medium">Brand</th>
                <th className="text-right px-4 py-2.5 font-medium">Our products (in stock)</th>
                <th className="text-right px-4 py-2.5 font-medium">Matched</th>
                <th className="text-right px-4 py-2.5 font-medium text-emerald-700">Cheapest in market</th>
                <th className="text-right px-4 py-2.5 font-medium">Within range</th>
                <th className="text-right px-5 py-2.5 font-medium text-red-700">Priciest in market</th>
              </tr>
            </thead>
            <tbody>
              {brandRows.map((b) => (
                <tr key={b.brand} onClick={() => { if (b.brand !== brand) { setBrand(b.brand); setPick({ site: '_market', rel: 'priciest' }); } }}
                  className={`border-b border-gray-50 cursor-pointer ${brand === b.brand ? 'bg-indigo-50' : 'hover:bg-gray-50'}`}>
                  <td className="px-5 py-2 font-medium text-gray-900">{b.brand}</td>
                  <td className="px-4 py-2 text-right text-gray-500 tabular-nums">{fmtNum(b.products)}</td>
                  <td className="px-4 py-2 text-right font-semibold tabular-nums">{fmtNum(b.matched)}</td>
                  {brand === b.brand ? (
                    <>
                      <td className="px-4 py-2 text-right"><Num n={b.cheapest} site="_market" r="cheapest" cls="text-emerald-600" /></td>
                      <td className="px-4 py-2 text-right"><Num n={b.range} site="_market" r="range" cls="text-gray-600" /></td>
                      <td className="px-5 py-2 text-right"><Num n={b.priciest} site="_market" r="priciest" cls="text-red-600" /></td>
                    </>
                  ) : (
                    <>
                      <td className="px-4 py-2 text-right text-emerald-700 tabular-nums">{fmtNum(b.cheapest)}</td>
                      <td className="px-4 py-2 text-right text-gray-600 tabular-nums">{fmtNum(b.range)}</td>
                      <td className="px-5 py-2 text-right text-red-700 tabular-nums">{fmtNum(b.priciest)}</td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Seller table for the selected brand */}
      <Card className="rounded-2xl overflow-hidden shadow-sm border border-gray-100">
        <div className="px-5 py-3 bg-gray-50 border-b border-gray-100 text-sm font-semibold text-gray-700">
          {brand} by seller <span className="font-normal text-gray-400">(click a number to list the products)</span>
        </div>
        <div className="overflow-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-white">
              <tr className="text-gray-500 border-b border-gray-100">
                <th className="text-left px-5 py-2 font-medium">Seller</th>
                <th className="text-left px-4 py-2 font-medium">Size data</th>
                <th className="text-right px-4 py-2 font-medium" title={`${brand} products listed on the seller's site (colour level)`}>Their products</th>
                <th className="text-right px-4 py-2 font-medium" title="Products in stock on both sides">Matched with us</th>
                <th className="text-right px-4 py-2 font-medium">{MIN_SIZES}+ sizes in stock</th>
                <th className="text-right px-4 py-2 font-medium text-emerald-600">We are cheaper</th>
                <th className="text-right px-4 py-2 font-medium">Equal</th>
                <th className="text-right px-5 py-2 font-medium text-red-600">We are pricier</th>
              </tr>
            </thead>
            <tbody>
              {sellerRows.map((s) => (
                <tr key={s.site} className={`border-b border-gray-50 ${pick.site === s.site ? 'bg-gray-50' : ''}`}>
                  <td className="px-5 py-2 font-medium text-gray-800">
                    {s.site}
                    {data?.dataDates?.[s.site] && data.dataDates[s.site] < (data.dataDates[OUR] || '') && (
                      <span className="ml-2 text-[11px] text-amber-600">data {data.dataDates[s.site]}</span>
                    )}
                  </td>
                  <td className={`px-4 py-2 ${s.sized ? 'text-gray-700' : 'text-gray-400'}`}>{s.sized ? 'Yes' : 'No'}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-gray-600">{s.stock == null ? '—' : fmtNum(s.stock)}</td>
                  <td className="px-4 py-2 text-right"><Num n={s.matched} site={s.site} r="matched" cls="text-gray-900 font-semibold" /></td>
                  <td className="px-4 py-2 text-right tabular-nums text-gray-600">{s.sized ? fmtNum(s.counted) : ''}</td>
                  <td className="px-4 py-2 text-right"><Num n={s.cheaper} site={s.site} r="cheaper" cls="text-emerald-600" /></td>
                  <td className="px-4 py-2 text-right"><Num n={s.equal} site={s.site} r="equal" cls="text-gray-600" /></td>
                  <td className="px-5 py-2 text-right"><Num n={s.pricier} site={s.site} r="pricier" cls="text-red-600 font-semibold" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Product list */}
      <Card className="rounded-2xl overflow-hidden shadow-sm border border-gray-100">
        <div className="px-5 py-3 bg-gray-50 border-b border-gray-100 flex flex-wrap items-center gap-3">
          <span className="text-sm font-semibold text-gray-800">{pickLabel}</span>
          <div className="relative">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search product name, ID…"
              className="border border-gray-300 rounded-lg pl-9 pr-3 h-9 text-sm w-64 bg-white" />
          </div>
          <span className="text-sm text-gray-400 tabular-nums ml-auto">{fmtNum(list.length)} products</span>
          <button type="button" onClick={downloadCsv} disabled={!list.length}
            className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg border border-gray-300 bg-white text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50">
            <FileDown className="w-4 h-4" /> CSV
          </button>
        </div>
        <div className="overflow-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-white border-b border-gray-100">
              <tr className="text-gray-600">
                <th className="text-left px-5 py-3 font-semibold">Product</th>
                <th className="text-right px-4 py-3 font-semibold text-indigo-700 bg-indigo-50">High5 (Us)</th>
                <th className="text-right px-4 py-3 font-semibold">{pick.site === '_market' ? 'Market min' : pick.site}</th>
                {pick.site === '_market' && <th className="text-right px-4 py-3 font-semibold">Market max</th>}
                <th className="text-right px-4 py-3 font-semibold">Difference</th>
                <th className="text-left px-5 py-3 font-semibold">{pick.site === '_market' ? 'Sellers' : 'Match'}</th>
              </tr>
            </thead>
            <tbody>
              {list.map(({ it, o, theirs, gap }) => (
                <tr key={it.id} className="border-b border-gray-50 hover:bg-gray-50 align-top">
                  <td className="px-5 py-2">
                    <div className="flex items-center gap-3">
                      {it.image ? (
                        <a href={it.urls?.[OUR] || undefined} target="_blank" rel="noreferrer" className="shrink-0">
                          <img src={it.image} alt="" loading="lazy" className="w-12 h-12 object-cover rounded-lg border border-gray-200" />
                        </a>
                      ) : <div className="w-12 h-12 rounded-lg bg-gray-100" />}
                      <div className="min-w-[220px]">
                        <div className="font-medium leading-tight">
                          {it.urls?.[OUR] ? (
                            <a href={it.urls[OUR]} target="_blank" rel="noreferrer"
                              className="text-gray-900 underline decoration-gray-300 underline-offset-2 hover:text-indigo-700 hover:decoration-indigo-400">
                              {it.name}
                            </a>
                          ) : it.name}
                        </div>
                        <div className="text-xs text-gray-400">{it.productId}{it.gender ? ` · ${it.gender}` : ''}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums bg-indigo-50/40">
                    {it.urls?.[OUR] ? (
                      <a href={it.urls[OUR]} target="_blank" rel="noreferrer"
                        className="font-semibold text-indigo-700 underline decoration-indigo-200 underline-offset-2 hover:decoration-indigo-500">
                        {fmt(it.ours)}
                      </a>
                    ) : <div className="font-semibold text-indigo-700">{fmt(it.ours)}</div>}
                    {it.own?.avail != null && <div className="text-[11px] text-gray-500">{it.own.avail}/{it.own.total} sizes</div>}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {o?.url ? (
                      <a href={o.url} target="_blank" rel="noreferrer"
                        className="font-medium underline decoration-gray-300 underline-offset-2 hover:decoration-gray-600">{fmt(theirs)}</a>
                    ) : <span className="font-medium">{fmt(theirs)}</span>}
                    {o && (o.sz?.avail != null
                      ? <div className={`text-[11px] ${o.counted ? 'text-gray-500' : 'text-amber-600'}`}>{o.sz.avail}{o.sz.total ? `/${o.sz.total}` : ''} sizes{o.counted ? '' : ' · not counted'}</div>
                      : <div className="text-[11px] text-gray-400">{o.sized ? 'no size data · not counted' : 'no size data'}</div>)}
                  </td>
                  {pick.site === '_market' && <td className="px-4 py-2 text-right tabular-nums">{fmt(it.marketMax)}</td>}
                  <td className={`px-4 py-2 text-right tabular-nums font-medium ${gap == null ? 'text-gray-300' : Math.abs(it.ours - theirs) < EQ_TL ? 'text-gray-500' : gap > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                    {gap == null ? '—' : `${gap > 0 ? '+' : ''}${gap.toFixed(1)}%`}
                    {gap != null && <div className="text-[11px] font-normal text-gray-400">{fmt(Math.abs(it.ours - theirs))}</div>}
                  </td>
                  <td className="px-5 py-2 text-xs">
                    {pick.site === '_market' ? (
                      <div className="flex flex-col gap-0.5">
                        {Object.entries(it.offers).filter(([, x]) => x.counted).sort((a, b) => a[1].price - b[1].price).map(([s, x]) => (
                          <a key={s} href={x.url || undefined} target="_blank" rel="noreferrer"
                            className={`tabular-nums hover:underline ${x.rel === 'pricier' ? 'text-red-600' : x.rel === 'cheaper' ? 'text-emerald-600' : 'text-gray-600'}`}>
                            {s} {fmt(x.price)}
                          </a>
                        ))}
                      </div>
                    ) : (
                      <span className={o?.method === 'model_gender' ? 'text-amber-600' : 'text-emerald-600'}
                        title={o?.method === 'model_gender' ? 'Model + gender match, colour not confirmed' : ''}>
                        {MATCH_LABEL[o?.method] || o?.method || ''}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {list.length === 0 && <div className="p-8 text-center text-gray-400">No products.</div>}
        </div>
      </Card>
    </div>
  );
}
